import * as Communicator from "@ts3d-hoops/web-viewer";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { BimApi } from "api/bim.api";
import { ConversionApi } from "api/conversion.api";
import { FileInfo, FileInfoResponse, PayloadOriginFile } from "common/define";
import { RootEpic } from "common/type-state";
import { merge, Observable } from "rxjs";
import {
    catchError,
    concatMap,
    distinctUntilChanged,
    filter,
    map,
    mergeMap,
    switchMap,
    withLatestFrom,
} from "rxjs/operators";
import { setAppLoading } from "./app.slice";
import { FileListHelper } from "./helper";
import { closeViewerObser } from "./helper/multiViewer.helper";
import { closeViewerComplete, firstFileDisplay } from "./multiViewer.slice";
import { GlobalState } from "common/global";

const initState: FileListHelper.FilesListState = {
    filesList: [],
    filesOrigin: [],
    loading: false,
    filesListThumnail: [],
    refreshFileList: "",
    fileInfoJsonMap: {},
};

const filesListSlice = createSlice({
    name: "filesList",
    initialState: initState,
    reducers: {
        getCacheFilesList: (
            state,
            action: PayloadAction<PayloadOriginFile[]>
        ) => {
            state.loading = true;
        },
        deleteFile(state, action: PayloadAction<ViewId>) {
            return;
        },
        updateFileListComplete(
            state,
            action: PayloadAction<FileListHelper.ResultFileListState>
        ) {
            const { newFileList, newFileThumnail, newFilesOrigin } =
                action.payload;
            newFileList && (state.filesList = newFileList);
            newFileThumnail && (state.filesListThumnail = newFileThumnail);
            newFilesOrigin && (state.filesOrigin = newFilesOrigin);
            state.loading = false;
        },
        setLoading(state, action: PayloadAction<boolean>) {
            state.loading = action.payload;
        },
        errorFilesList: (state) => {
            state.loading = false;
        },
        uploadThumnail: (
            state,
            action: PayloadAction<{ file: FileInfo; thumnailSrc: string }>
        ) => {
            return;
        },
        refreshFilesList: (state, action: PayloadAction<undefined>) => {
            state.refreshFileList = Communicator.createUuid();
        },
        getFileInfoJson(state, action) {
            state.loading = true;
        },
        setFileInfoJson(
            state,
            action: PayloadAction<{ modelFileId: string; data: any }>
        ) {
            const { modelFileId, data } = action.payload;
            state.fileInfoJsonMap[modelFileId] = data;
            GlobalState.setFileInfoJson(modelFileId, data);
            state.loading = false;
        },
    },
});

/**
 * Resolves (get, else create) a BIM ModelFileId for a FileInfo the conversion api returned
 * without one, then persists it back to the StreamFile. Mutates `x` in place.
 */
async function backfillModelFileId(x: FileInfoResponse): Promise<void> {
    if (x.modelFileId != null && x.modelFileId !== "") return;
    if (!x.streamLocation) return;
    console.debug("[filesList.slice] backfillModelFileId: called", {
        baseFileId: x.baseFileId,
        filename: x.filename,
    });
    x.fileLocation = `${x.streamLocation}/${
        x.filename.split(".")[0]
    }.assemblytree.xml`;
    x.modelFileId = await BimApi.getModelFileId(x).toPromise();
    if (x.modelFileId == null || x.modelFileId === "")
        x.modelFileId = await BimApi.createModelFileId(x).toPromise();
    if (x.modelFileId != null && x.modelFileId !== "") {
        ConversionApi.updateModelId({
            fileName: x.filename,
            baseFileId: x.baseFileId,
            baseMinorRev: x.baseMinorRev ?? 0,
            baseMajorRev: x.baseMajorRev ?? 0,
            modelFileId: x.modelFileId,
        }).subscribe({
            error: (err) =>
                console.error(
                    "[filesList.slice] backfillModelFileId: updateModelId failed",
                    { baseFileId: x.baseFileId, filename: x.filename, err }
                ),
        });
    }
    console.debug("[filesList.slice] backfillModelFileId: ok", {
        baseFileId: x.baseFileId,
        filename: x.filename,
        modelFileId: x.modelFileId,
    });
}

/**
 * Effect get cache file list + transform file item
 * @param action$
 * @param state$
 * @returns
 */
const filesList$: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(getCacheFilesList.match),
        distinctUntilChanged(),
        withLatestFrom(state$),
        switchMap(([action, state]) => {
            const list = action.payload;
            const preList = state.filesList.filesOrigin;
            const arrDiff = FileListHelper.filterListAdd(list, preList);
            FileListHelper.logFileOpenDebug(
                "filesList$ received getCacheFilesList",
                {
                    requested: FileListHelper.summarizePayloadOriginFiles(list),
                    existingOrigin: FileListHelper.summarizeFileInfos(preList),
                    diff: arrDiff
                        ? FileListHelper.summarizePayloadOriginFiles(arrDiff)
                        : [],
                    multiViewerState: {
                        viewActive: state.multiViewer.viewActive,
                        listViewerVisible: state.multiViewer.listViewerVisible,
                    },
                }
            );
            if (arrDiff) {
                return ConversionApi.getCacheByListFile(arrDiff).pipe(
                    concatMap(
                        (result) =>
                            new Observable<FileInfoResponse[]>((subscriber) => {
                                const newResult: FileInfoResponse[] = [];
                                // Each item settles on its own: a failing BIM backfill call for
                                // one file (e.g. a 2D sheet with no ModelFileId yet) must not
                                // keep that item out of newResult — otherwise the length check
                                // below never passes and the whole file list never emits.
                                result.forEach(async (x) => {
                                    try {
                                        await backfillModelFileId(x);
                                    } catch (err) {
                                        console.error(
                                            "[filesList.slice] filesList$: modelFileId backfill failed",
                                            {
                                                baseFileId: x.baseFileId,
                                                filename: x.filename,
                                                err,
                                            }
                                        );
                                    }
                                    newResult.push(x);
                                    if (newResult.length === result.length) {
                                        FileListHelper.logFileOpenDebug(
                                            "filesList$ resolved cache payload",
                                            {
                                                requestedDiff:
                                                    FileListHelper.summarizePayloadOriginFiles(
                                                        arrDiff
                                                    ),
                                                cacheResult: newResult.map(
                                                    (item) => ({
                                                        baseFileId:
                                                            item.baseFileId,
                                                        baseMajorRev:
                                                            item.baseMajorRev ??
                                                            0,
                                                        baseMinorRev:
                                                            item.baseMinorRev ??
                                                            0,
                                                        filename: item.filename,
                                                        modelFileId:
                                                            item.modelFileId,
                                                        streamLocation:
                                                            item.streamLocation,
                                                        isRootModel:
                                                            item.isRootModel,
                                                    })
                                                ),
                                            }
                                        );
                                        subscriber.next(newResult);
                                        subscriber.complete();
                                    }
                                });
                            })
                    ),
                    concatMap((newResult) =>
                        FileListHelper.addFilesList$(newResult, state.filesList)
                    ),
                    mergeMap((resultAdd) => {
                        const shouldDisplayFirstFile =
                            preList.length === 0 ||
                            state.multiViewer.listViewerVisible.length === 0 ||
                            !state.multiViewer.viewActive.baseFileId;

                        FileListHelper.logFileOpenDebug(
                            "filesList$ computed updateFileListComplete",
                            {
                                shouldDisplayFirstFile,
                                preListLength: preList.length,
                                viewActive: state.multiViewer.viewActive,
                                listViewerVisible:
                                    state.multiViewer.listViewerVisible,
                                newFileList: resultAdd.newFileList
                                    ? FileListHelper.summarizeFileInfos(
                                          resultAdd.newFileList
                                      )
                                    : [],
                                newFilesOrigin: resultAdd.newFilesOrigin
                                    ? FileListHelper.summarizeFileInfos(
                                          resultAdd.newFilesOrigin
                                      )
                                    : [],
                            }
                        );

                        if (!shouldDisplayFirstFile) {
                            return [
                                filesListSlice.actions.updateFileListComplete(
                                    resultAdd
                                ),
                            ];
                        } else {
                            return [
                                filesListSlice.actions.updateFileListComplete(
                                    resultAdd
                                ),
                                firstFileDisplay(),
                            ];
                        }
                    }),
                    catchError((err) => {
                        FileListHelper.logFileOpenDebug("filesList$ failed", {
                            error:
                                err instanceof Error
                                    ? { message: err.message, stack: err.stack }
                                    : err,
                        });
                        return [errorFilesList()];
                    })
                );
            }
            FileListHelper.logFileOpenDebug("filesList$ found no diff to add", {
                requested: FileListHelper.summarizePayloadOriginFiles(list),
                existingOrigin: FileListHelper.summarizeFileInfos(preList),
            });
            return [filesListSlice.actions.setLoading(false)];
        })
    );
const deleteFile$: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(deleteFile.match),
        withLatestFrom(state$),
        concatMap(([{ payload: viewId }, state]) => {
            return merge(
                [setAppLoading(true)],
                closeViewerObser(viewId, state.multiViewer).pipe(
                    mergeMap((resultClose) => {
                        return merge(
                            [closeViewerComplete(resultClose)],
                            FileListHelper.deleteFileObser(
                                viewId,
                                state.filesList
                            ).pipe(
                                mergeMap((resultDelete) => [
                                    setAppLoading(false),
                                    filesListSlice.actions.updateFileListComplete(
                                        resultDelete
                                    ),
                                ]),
                                catchError((err) => [setAppLoading(false)])
                            )
                        );
                    })
                )
            );
        })
    );

const uploadThumnail$: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(uploadThumnail.match),
        withLatestFrom(state$),
        switchMap(([action, state]) => {
            const { file, thumnailSrc } = action.payload;
            return ConversionApi.uploadThumnail(file, thumnailSrc).pipe(
                mergeMap((res) => {
                    console.log(res);
                    return [];
                }),
                catchError((err) => {
                    console.log(err);
                    return [];
                })
            );
        })
    );

const getFileInfoJson$: RootEpic = (action$, state$) => {
    return action$.pipe(
        filter(getFileInfoJson.match),
        withLatestFrom(state$),
        switchMap(([action, state]) => {
            const viewId = action.payload;
            const fileInfo = state.filesList.filesOrigin.find(
                (f) => f.viewId === viewId
            );
            if (!fileInfo) {
                return [];
            }
            const info = state.filesList.fileInfoJsonMap[fileInfo.modelFileId];
            if (info) {
                return [];
            }
            return BimApi.getFileInfoJson(
                `./${fileInfo.baseFileId}.${fileInfo.baseMajorRev}.${fileInfo.baseMinorRev}`
            ).pipe(
                map((jsonRes) => {
                    return setFileInfoJson({
                        modelFileId: fileInfo.modelFileId,
                        data: jsonRes,
                    });
                }),
                catchError((err) => {
                    console.log(err);
                    return [setLoading(false)];
                })
            );
        })
    );
};

export const FilesListEpics = [
    filesList$,
    deleteFile$,
    uploadThumnail$,
    getFileInfoJson$,
];

export const {
    getCacheFilesList,
    errorFilesList,
    deleteFile,
    uploadThumnail,
    refreshFilesList,
    getFileInfoJson,
    setFileInfoJson,
    setLoading,
} = filesListSlice.actions;
export default filesListSlice.reducer;
